import { useEffect, useRef } from 'react';

// -------------------- WebGL Wave Configuration
// Controls the intensity of the analog TV wave distortion effect

const WAVE_INTENSITY_DESKTOP = 0.0045; // Desktop: subtle waves
const WAVE_INTENSITY_TOUCH = 0.012;    // Touch devices: more visible waves (3x stronger)
const WAVE_SPEED_DESKTOP = 1.2;        // Desktop: slower movement
const WAVE_SPEED_TOUCH = 2.4;          // Touch devices: faster movement (2x speed)

/* Intensity notes:
0.0045 = Desktop intensity (subtle)
0.012 = Touch device intensity (3x stronger, still analog-looking)
0.018 = Original full intensity (dramatic)
0.002 = Very subtle waves
0.01 = Medium intensity
*/

// --------------------

interface WebGLDistortionProps {
  isActive: boolean;
  className?: string;
  imageElement?: HTMLImageElement | null;
  keepAnimatingDuringFadeOut?: boolean;
  isTouchDevice?: boolean;
}

export default function WebGLDistortion({ isActive, className, imageElement, keepAnimatingDuringFadeOut = false, isTouchDevice = false }: WebGLDistortionProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | null>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const textureRef = useRef<WebGLTexture | null>(null);
  const startTimeRef = useRef<number>(0);
  // const pauseTimeRef = useRef<number>(0);
  const shouldStopRef = useRef<boolean>(false);

  // Select wave parameters based on device type
  const waveIntensity = isTouchDevice ? WAVE_INTENSITY_TOUCH : WAVE_INTENSITY_DESKTOP;
  const waveSpeed = isTouchDevice ? WAVE_SPEED_TOUCH : WAVE_SPEED_DESKTOP;

  // Effect to initialize and run WebGL animation
  useEffect(() => {

    // Clean up when no canvas or image
    if (!canvasRef.current || !imageElement) {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }

    // If not active and not keeping animation during fade out, stop immediately
    if (!isActive && !keepAnimatingDuringFadeOut) {
      // Stop animation immediately if not active and not keeping during fade
      shouldStopRef.current = true;
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }

    // Reset stop flag and handle timing when becoming active
    if (isActive) {
      shouldStopRef.current = false;
      // Only reset start time if we're starting fresh (not resuming)
      if (startTimeRef.current === 0) {
        startTimeRef.current = Date.now();
      }
    }

    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl');

    // If WebGL not supported, exit
    if (!gl) {
      console.warn('WebGL not supported, falling back to simple animation');
      return;
    }

    glRef.current = gl;

    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;

      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `;

    // Fragment shader with horizontal wave displacement
    const fragmentShaderSource = `
      precision mediump float;
      varying vec2 v_texCoord;
      uniform float u_time;
      uniform vec2 u_resolution;
      uniform sampler2D u_texture;

      void main() {
        // Fix vertical flip by inverting Y coordinate
        vec2 uv = vec2(v_texCoord.x, 1.0 - v_texCoord.y);

        // Create horizontal wave displacement that moves from top to bottom
        float waveFrequency = 20.0;    // More waves across height
        float waveAmplitude = ${waveIntensity};  // Dynamic based on device type
        float waveSpeed = ${waveSpeed};         // Dynamic speed based on device type

        // Create uniform waves (single frequency for consistency)
        // Use only one primary wave for uniform appearance
        float primaryWave = sin(uv.y * waveFrequency - u_time * waveSpeed) * waveAmplitude;

        // Add very subtle secondary wave for slight variation
        float secondaryWave = sin(uv.y * waveFrequency * 0.5 - u_time * waveSpeed * 0.3) * waveAmplitude * 0.2;

        // Combine for uniform but not completely flat waves
        float totalDisplacement = primaryWave + secondaryWave;

        // Apply horizontal displacement to UV coordinates
        vec2 distortedUV = vec2(uv.x + totalDisplacement, uv.y);

        // Sample the pause image with distorted coordinates
        // Use CLAMP_TO_EDGE behavior - stretch edge pixels instead of showing black
        distortedUV.x = clamp(distortedUV.x, 0.0, 1.0);
        distortedUV.y = clamp(distortedUV.y, 0.0, 1.0);

        vec4 finalColor = texture2D(u_texture, distortedUV);

        // Add very subtle color shift for slight analog effect
        float colorShift = totalDisplacement * 0.5;
        finalColor.r += colorShift * 0.02;
        finalColor.b -= colorShift * 0.02;

        // Make completely opaque
        gl_FragColor = vec4(finalColor.rgb, 1.0);
      }
    `;

    // Create and compile shaders
    const createShader = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }

      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) return;

    // Create program
    const program = gl.createProgram();
    if (!program) return;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program linking error:', gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);
    programRef.current = program;

    // Set up geometry (full screen quad)
    const positions = new Float32Array([
      -1, -1,  0, 0,
       1, -1,  1, 0,
      -1,  1,  0, 1,
      -1,  1,  0, 1,
       1, -1,  1, 0,
       1,  1,  1, 1,
    ]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const texCoordLocation = gl.getAttribLocation(program, 'a_texCoord');

    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 16, 0);

    gl.enableVertexAttribArray(texCoordLocation);
    gl.vertexAttribPointer(texCoordLocation, 2, gl.FLOAT, false, 16, 8);

    // Create texture from the pause image
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    // Set texture parameters
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Validate image before uploading to texture
    if (!imageElement.complete || imageElement.naturalWidth === 0 || imageElement.naturalHeight === 0) {
      // console.warn('WebGL: Image not fully loaded or invalid, skipping texture upload');
      return;
    }

    // Upload the image to the texture
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageElement);

    textureRef.current = texture;

    // Animation loop
    const render = () => {
      if (!canvas || !gl || !program || !textureRef.current || shouldStopRef.current) return;

      // Set canvas size
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }

      // Bind texture
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textureRef.current);

      // Set uniforms
      const timeLocation = gl.getUniformLocation(program, 'u_time');
      const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
      const textureLocation = gl.getUniformLocation(program, 'u_texture');

      gl.uniform1f(timeLocation, (Date.now() - startTimeRef.current) / 1000);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1i(textureLocation, 0);

      // Clear and draw
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      animationIdRef.current = requestAnimationFrame(render);
    };

    // Start animation
    if (startTimeRef.current === 0) {
      startTimeRef.current = Date.now();
    }
    render();

    // Cleanup function
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [isActive, imageElement, keepAnimatingDuringFadeOut, waveIntensity, waveSpeed]);

  // Effect to handle delayed stop when fade out starts
  useEffect(() => {
    if (!isActive && keepAnimatingDuringFadeOut) {
      // Delay stopping animation to let fade-out complete
      const timer = setTimeout(() => {
        shouldStopRef.current = true;
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
      }, 324); // Match the fade-out delay from modal

      return () => clearTimeout(timer);
    }
  }, [isActive, keepAnimatingDuringFadeOut]);

  // Don't render canvas at all if not active and not keeping during fade out
  if (!isActive && !keepAnimatingDuringFadeOut) return null;

  // -------------------- Render

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        opacity: 1.0
      }}
    />
  );

}
